import {
  TagDefinition,
  useCreateTagDefinitionMutation,
  useDeleteTagDefinitionMutation,
  useUpdateTagDefinitionMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseTagDefinitionMutationProps = {}

export type UseTagDefinitionMutationResult = {
  createTagDefinition: (tagDefinition: Partial<TagDefinition>, callback: EntityMutationCallback<TagDefinition> | undefined) => void
  updateTagDefinition: (tagDefinition: Partial<TagDefinition>, callback: EntityMutationCallback<TagDefinition> | undefined) => void
  deleteTagDefinition: (tagDefinitionId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useTagDefinitionMutation = (props: UseTagDefinitionMutationProps = {}): UseTagDefinitionMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createTagDefinition, {isLoading: isCreating}] = useCreateTagDefinitionMutation()
  const [_updateTagDefinition, {isLoading: isUpdating}] = useUpdateTagDefinitionMutation()
  const [_deleteTagDefinition, {isLoading: isDeleting}] = useDeleteTagDefinitionMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useTagDefinitionUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.tagdefinition", "Tag Definition"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useTagDefinitionUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useTagDefinitionUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.tagdefinition", "Tag Definition"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useTagDefinitionUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useTagDefinitionUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.tagdefinition", "Tag Definition"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useTagDefinitionUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<TagDefinition>, callback: EntityMutationCallback<TagDefinition> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagdefinition", "Tag Definition") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<TagDefinition>, callback: EntityMutationCallback<TagDefinition> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagdefinition", "Tag Definition") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagdefinition", "Tag Definition") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((tagDefinitionId: string, callback: EntityMutationCallback<TagDefinition> | undefined) => {
    const promise = _deleteTagDefinition({id: tagDefinitionId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteTagDefinition, handleDeleteResult])

  const createTagDefinition = useCallback((tagDefinition: Partial<TagDefinition>, callback: EntityMutationCallback<TagDefinition> | undefined) => {
    const promise = _createTagDefinition(tagDefinition).unwrap()
    handleCreateResult(promise, callback)
  }, [_createTagDefinition, handleCreateResult])

  const updateTagDefinition = useCallback((tagDefinition: Partial<TagDefinition>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateTagDefinition(tagDefinition).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateTagDefinition, handleUpdateResult])

  const deleteTagDefinition = useCallback((tagDefinitionId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.tagdefinition", "Tag Definition"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.tagdefinition.confirm", "Are you sure you want to delete this tag definition?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(tagDefinitionId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createTagDefinition: createTagDefinition,
    updateTagDefinition: updateTagDefinition,
    deleteTagDefinition: deleteTagDefinition,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
